import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import api, { govtApi } from '../../services/api';

interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'general' | 'test' | 'url';
  link_type?: string;
  link_id?: number;
  external_url?: string;
  icon?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  is_active: boolean;
  created_at: string;
  expires_at?: string;
  linked_test_title?: string;
}

// FCM topic an exam maps to — MUST match pushNotificationService.ts's
// `examTopic()` on the mobile client, or the push reaches zero subscribers.
const examTopic = (examId: string) => `exam_${examId.replace(/-/g, '_')}`;

const CATEGORY_TOPICS = [
  { value: 'general',         label: 'All Students (General)' },
  { value: 'exam_based',      label: 'All Exam-Based Subscribers' },
  { value: 'personalization', label: 'Personalised Insights Subscribers' },
  { value: 'reminders',       label: 'Reminder Subscribers' },
];

interface ExamOption { id: string; name: string; code?: string; }

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [tests, setTests] = useState<any[]>([]);
  const [exams, setExams] = useState<ExamOption[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });

  // Push notification state
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [pushLoading, setPushLoading]       = useState(false);
  const [pushForm, setPushForm] = useState({
    title: '',
    body: '',
    topic: 'general',
    data: '{}',
  });
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general' as 'general' | 'test' | 'url',
    link_type: '',
    link_id: '',
    external_url: '',
    icon: 'bell',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    is_active: true,
    expires_at: '',
  });

  useEffect(() => {
    loadNotifications();
    loadTests();
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      const response = await govtApi.get('/govt/exams');
      setExams(Array.isArray(response.data) ? response.data : (response.data?.data ?? []));
    } catch (error) {
      console.error('Error loading exams:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications/all');
      setNotifications(response.data.data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
      showSnackbar('Failed to load notifications', 'error');
    }
  };

  const loadTests = async () => {
    try {
      // "Test Link" notifications deep-link via the mobile app's legacy testService
      // (GET /tests/:id, backed by the `tests` table) — not the govt exam tests, which
      // that lookup can't resolve. Must stay pointed at /admin/tests.
      const response = await api.get('/admin/tests');
      const data = response.data;
      setTests(Array.isArray(data) ? data : (data?.data ?? []));
    } catch (error) {
      console.error('Error loading tests:', error);
    }
  };

  const handleOpenDialog = (notification?: Notification) => {
    if (notification) {
      setEditingNotification(notification);
      setFormData({
        title: notification.title,
        message: notification.message,
        type: notification.type,
        link_type: notification.link_type || '',
        link_id: notification.link_id?.toString() || '',
        external_url: notification.external_url || '',
        icon: notification.icon || 'bell',
        priority: notification.priority,
        is_active: notification.is_active,
        expires_at: notification.expires_at || '',
      });
    } else {
      setEditingNotification(null);
      setFormData({
        title: '',
        message: '',
        type: 'general',
        link_type: '',
        link_id: '',
        external_url: '',
        icon: 'bell',
        priority: 'normal',
        is_active: true,
        expires_at: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNotification(null);
  };

  const handleSubmit = async () => {
    try {
      const payload: any = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        icon: formData.icon,
        priority: formData.priority,
        is_active: formData.is_active,
      };

      if (formData.type === 'test') {
        payload.link_type = 'test';
        payload.link_id = formData.link_id;
      } else if (formData.type === 'url') {
        payload.link_type = 'url';
        payload.external_url = formData.external_url;
      }

      if (formData.expires_at) {
        payload.expires_at = formData.expires_at;
      }

      if (editingNotification) {
        await api.put(`/notifications/${editingNotification.id}`, payload);
        showSnackbar('Notification updated successfully', 'success');
      } else {
        await api.post('/notifications', payload);
        showSnackbar('Notification created successfully', 'success');
      }

      handleCloseDialog();
      loadNotifications();
    } catch (error) {
      console.error('Error saving notification:', error);
      showSnackbar('Failed to save notification', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        await api.delete(`/notifications/${id}`);
        showSnackbar('Notification deleted successfully', 'success');
        loadNotifications();
      } catch (error) {
        console.error('Error deleting notification:', error);
        showSnackbar('Failed to delete notification', 'error');
      }
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      await api.patch(`/notifications/${id}/toggle`);
      showSnackbar('Status updated successfully', 'success');
      loadNotifications();
    } catch (error) {
      console.error('Error toggling notification:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const sendPush = async () => {
    if (!pushForm.title || !pushForm.body) {
      showSnackbar('Title and body are required', 'error');
      return;
    }
    setPushLoading(true);
    try {
      let parsedData: Record<string, string> = {};
      try { parsedData = JSON.parse(pushForm.data || '{}'); } catch { /* ignore */ }
      await api.post('/notifications/send-push', {
        title: pushForm.title,
        body: pushForm.body,
        topic: pushForm.topic,
        data: parsedData,
      });
      showSnackbar('Push notification sent successfully!', 'success');
      setPushDialogOpen(false);
      setPushForm({ title: '', body: '', topic: 'general', data: '{}' });
    } catch (error) {
      console.error('Error sending push notification:', error);
      showSnackbar('Failed to send push notification', 'error');
    } finally {
      setPushLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'test': return 'success';
      case 'url': return 'info';
      case 'general': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NotificationsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Notifications Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage system notifications and announcements
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<NotificationsIcon />}
            onClick={() => setPushDialogOpen(true)}
            size="large">
            Send Push
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="large">
            Create Notification
          </Button>
        </Box>
      </Box>

      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.map((notification) => (
                <TableRow key={notification.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {notification.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                      {notification.message}
                    </Typography>
                    {notification.linked_test_title && (
                      <Chip label={notification.linked_test_title} size="small" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={notification.type} size="small" color={getTypeColor(notification.type) as any} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={notification.priority}
                      size="small"
                      color={getPriorityColor(notification.priority) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={notification.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={notification.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(notification.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(notification.id)}
                      title={notification.is_active ? 'Deactivate' : 'Activate'}>
                      {notification.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(notification)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(notification.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingNotification ? 'Edit Notification' : 'Create New Notification'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              fullWidth
              multiline
              rows={3}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                label="Type">
                <MenuItem value="general">General Announcement</MenuItem>
                <MenuItem value="test">Test Link</MenuItem>
                <MenuItem value="url">External URL</MenuItem>
              </Select>
            </FormControl>

            {formData.type === 'test' && (
              <FormControl fullWidth>
                <InputLabel>Select Test</InputLabel>
                <Select
                  value={formData.link_id}
                  onChange={(e) => setFormData({ ...formData, link_id: e.target.value })}
                  label="Select Test">
                  {tests.map((test) => (
                    <MenuItem key={test.id} value={test.id}>
                      {test.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {formData.type === 'url' && (
              <TextField
                label="External URL"
                value={formData.external_url}
                onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
                fullWidth
                placeholder="https://example.com"
              />
            )}

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                label="Priority">
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Icon Name (MaterialCommunityIcons)"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              fullWidth
              placeholder="bell, information, alert-circle, etc."
            />

            <TextField
              label="Expires At (Optional)"
              type="datetime-local"
              value={formData.expires_at}
              onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.title || !formData.message}>
            {editingNotification ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Push Notification Dialog */}
      <Dialog open={pushDialogOpen} onClose={() => setPushDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon color="primary" />
            Send Push Notification
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Notification Title"
              value={pushForm.title}
              onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })}
              fullWidth
              required
              placeholder="e.g. New Test Available!"
            />
            <TextField
              label="Notification Body"
              value={pushForm.body}
              onChange={(e) => setPushForm({ ...pushForm, body: e.target.value })}
              fullWidth
              multiline
              rows={3}
              required
              placeholder="e.g. A new KSP PC daily test is now live. Attempt it before it expires!"
            />
            <FormControl fullWidth>
              <InputLabel>Target Audience</InputLabel>
              <Select
                value={pushForm.topic}
                onChange={(e) => setPushForm({ ...pushForm, topic: e.target.value })}
                label="Target Audience">
                {CATEGORY_TOPICS.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
                {exams.map((exam) => (
                  <MenuItem key={exam.id} value={examTopic(exam.id)}>
                    {exam.name} Aspirants
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Extra Data (JSON, optional)"
              value={pushForm.data}
              onChange={(e) => setPushForm({ ...pushForm, data: e.target.value })}
              fullWidth
              placeholder='{"screen": "ScheduledTests"}'
              helperText="Optional JSON payload for deep-linking"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPushDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={sendPush}
            variant="contained"
            disabled={pushLoading || !pushForm.title || !pushForm.body}
            startIcon={pushLoading ? undefined : <NotificationsIcon />}>
            {pushLoading ? 'Sending...' : 'Send Push'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsPage;
